import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useDocFormFiller } from "../../hooks/useDocFormFiller";
import { toast } from "react-toastify";
import { Button, Chip, Paper } from "@mui/material";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import GetAppIcon from "@material-ui/icons/GetApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DynamicForm, { isFieldCompleted, normalizeCheckboxValue } from "../../components/DynamicForm";
import DocumentEditor from "../../components/documentEditor/documentEditor";
import SubmittedAnswersReview from "../../components/SubmittedAnswersReview/SubmittedAnswersReview";
import { makeStyles } from "@material-ui/core/styles";
import Loading from "Components/loading";
import { ToastContainer } from "react-toastify";
import {
	fillDocForm,
	updateSignature,
	isMagicLinkFormCompleted,
	isMagicLinkSession,
	markMagicLinkFormCompleted,
	syncMagicLinkSessionFromValidate,
} from "Services/docForm.service";
import { useSignatureForms } from "DocPlace/DocForms/hooks/useSignatureForms";
import { useFormCollaboration } from "../../hooks/useFormCollaboration";
import { getSignerDisplayNameFromStorage } from "../../utils/initials";

const useStyles = makeStyles((theme) => ({
	root: {
		flex: 1,
		minHeight: 0,
		height: "100%",
		maxHeight: "100%",
		width: "100%",
		maxWidth: "100%",
		padding: theme.spacing(0.5, 2, 1),
		display: "flex",
		flexDirection: "column",
		backgroundColor: "#E4E9EF",
		boxSizing: "border-box",
		overflow: "hidden",
		[theme.breakpoints.down("md")]: {
			flex: "0 0 auto",
			height: "auto",
			maxHeight: "none",
			overflow: "visible",
			padding: theme.spacing(0.5, 1, 2),
		},
	},
	headerContainer: {
		flexShrink: 0,
		marginBottom: theme.spacing(0.5),
		padding: 0,
		width: "100%",
	},
	breadcrumb: {
		fontSize: 13,
		color: "#6b7280",
		marginBottom: 6,
		display: "flex",
		alignItems: "center",
		gap: 4,
	},
	breadcrumbLink: {
		color: "#006ac6",
		textDecoration: "none",
		"&:hover": { textDecoration: "underline" },
	},
	headerRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		flexWrap: "wrap",
		gap: 8,
	},
	headerTitleRow: {
		display: "flex",
		alignItems: "center",
		gap: 10,
	},
	headerTitle: {
		fontWeight: 700,
		fontSize: "1.375rem",
		lineHeight: 1.3,
		letterSpacing: "-0.02em",
		color: "#111827",
		margin: 0,
		[theme.breakpoints.down("sm")]: {
			fontSize: "1.125rem",
		},
	},
	headerActions: {
		display: "flex",
		alignItems: "center",
		gap: 8,
	},
	headerActionBtn: {
		textTransform: "none",
		fontSize: 13,
		borderColor: "#e5e7eb",
		color: "#374151",
		"&:hover": { borderColor: "#d1d5db" },
	},
	headerSubtitle: {
		fontWeight: 400,
		fontSize: "16px",
		color: "#006ac6",
	},
	activityTimeline: {
		marginTop: theme.spacing(2),
		paddingTop: theme.spacing(2),
		borderTop: "1px solid #e5e7eb",
	},
	activityTitle: {
		fontSize: 13,
		fontWeight: 600,
		color: "#374151",
		marginBottom: theme.spacing(1.5),
		textTransform: "uppercase",
		letterSpacing: "0.05em",
	},
	activityItem: {
		display: "flex",
		alignItems: "flex-start",
		gap: 10,
		marginBottom: theme.spacing(1.25),
	},
	activityDot: {
		width: 8,
		height: 8,
		borderRadius: "50%",
		backgroundColor: "#009A17",
		flexShrink: 0,
		marginTop: 5,
	},
	activityText: {
		fontSize: 13,
		color: "#374151",
		lineHeight: 1.4,
	},
	activityTime: {
		fontSize: 12,
		color: "#9ca3af",
		display: "block",
		marginTop: 1,
	},
	container: {
		flex: 1,
		minHeight: 0,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: theme.spacing(2),
		overflow: "hidden",
		width: "100%",
		maxWidth: "100%",
		[theme.breakpoints.down("md")]: {
			flexDirection: "column",
			alignItems: "stretch",
			flex: "0 0 auto",
			minHeight: "auto",
			overflow: "visible",
			gap: theme.spacing(1.5),
		},
	},
	pdfContainer: {
		flex: "7 1 0",
		alignSelf: "stretch",
		minWidth: 0,
		minHeight: 0,
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		maxHeight: "calc(100vh - 120px)",
		[theme.breakpoints.down("md")]: {
			flex: "none",
			width: "100%",
			maxWidth: "100%",
			minHeight: 200,
			// Scroll the whole PDF card here (reliable touch scrolling on iOS/Android)
			maxHeight: "min(52vh, 420px)",
			order: 2,
			overflowX: "auto",
			overflowY: "auto",
			WebkitOverflowScrolling: "touch",
			overscrollBehavior: "contain",
			touchAction: "pan-x pan-y pinch-zoom",
		},
	},
	pdfPaper: {
		flex: 1,
		minHeight: 0,
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		height: "100%",
		borderRadius: 12,
		backgroundColor: "#fff",
		boxShadow:
			"0 10px 40px rgba(0, 38, 86, 0.12), 0 2px 10px rgba(0, 0, 0, 0.08)",
		border: "1px solid rgba(0, 0, 0, 0.06)",
		[theme.breakpoints.down("md")]: {
			borderRadius: 10,
			minHeight: "auto",
			maxHeight: "none",
			flex: "0 0 auto",
			overflow: "visible",
		},
	},
	formContainer: {
		flex: "3 1 0",
		minWidth: 0,
		maxHeight: "100%",
		overflow: "auto",
		alignSelf: "flex-start",
		flexShrink: 0,
		padding: theme.spacing(2.5, 3, 3),
		backgroundColor: "#fff",
		borderRadius: 12,
		display: "flex",
		flexDirection: "column",
		boxShadow:
			"0 12px 40px rgba(0, 38, 86, 0.12), 0 4px 14px rgba(0, 0, 0, 0.06)",
		border: "1px solid rgba(255, 255, 255, 0.9)",
		[theme.breakpoints.down("md")]: {
			order: 1,
			width: "100%",
			maxWidth: "100%",
			flex: "none",
			alignSelf: "stretch",
			maxHeight: "none",
			padding: theme.spacing(2),
			borderRadius: 10,
		},
	},
	buttonContainer: {
		display: "flex",
		justifyContent: "flex-end",
		backgroundColor: "white",
		marginTop: theme.spacing(3),
		marginBottom: 0,
		paddingBottom: theme.spacing(0.5),
		[theme.breakpoints.down("md")]: {
			marginTop: theme.spacing(2),
		},
	},
	rejectButton: {
		backgroundColor: "#fff",
		color: "#000",
		"&:hover": {
			backgroundColor: "#fff",
		},
		width: "100%",
		padding: "8px 16px",
		borderRadius: "10px",
	},
	completeButton: {
		backgroundColor: "#006ac6",
		color: "white",
		"&:hover": {
			backgroundColor: "#006ac6",
		},
		width: "100%",
		padding: "8px 16px",
		borderRadius: "10px",
	},
	formTitle: {
		fontWeight: 700,
		fontSize: "1.25rem",
		color: "#111827",
		marginBottom: theme.spacing(0.5),
		[theme.breakpoints.down("sm")]: {
			fontSize: "1.1rem",
		},
	},
	formSubtitle: {
		fontWeight: 400,
		fontSize: "0.875rem",
		color: "#6b7280",
		marginBottom: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			fontSize: "0.8125rem",
		},
	},
	divider: {	
		width: "100%",
		height: "1px",
		backgroundColor: "#006ac6",
		marginBottom: theme.spacing(1),
	},
	formContent: {
		flexShrink: 0,
	},
}));

function Header({
	readOnly,
	templateName,
	urlPdf,
}: {
	readOnly?: boolean;
	templateName?: string;
	urlPdf?: string;
}) {
	const classes = useStyles();
	const navigate = useNavigate();

	const handleDownload = () => {
		if (!urlPdf) return;
		const a = document.createElement("a");
		a.href = urlPdf;
		a.download = templateName ? `${templateName}.pdf` : "form.pdf";
		a.target = "_blank";
		a.rel = "noopener noreferrer";
		a.click();
	};

	return (
		<div className={classes.headerContainer}>
			<div className={classes.breadcrumb}>
				<a href="/docForms" className={classes.breadcrumbLink} onClick={(e) => { e.preventDefault(); navigate("/docForms"); }}>
					DocPlace Forms
				</a>
				{" › "}
				<span>{readOnly ? "Completed" : "Assigned"}</span>
				{templateName && <>{" › "}<span>{templateName}</span></>}
			</div>
			<div className={classes.headerRow}>
				<div className={classes.headerTitleRow}>
					<h1 className={classes.headerTitle}>
						{templateName || (readOnly ? "Your submitted form" : "Review and Complete")}
					</h1>
					{readOnly && (
						<Chip
							label="Submitted"
							size="small"
							icon={<CheckCircleIcon style={{ fontSize: 14, color: "#009A17" }} />}
							sx={{
								backgroundColor: "#f0fdf4",
								color: "#166534",
								fontSize: 12,
								fontWeight: 600,
								borderRadius: "6px",
								height: 24,
							}}
						/>
					)}
				</div>
				{readOnly && (
					<div className={classes.headerActions}>
						{urlPdf && (
							<Button
								variant="outlined"
								size="small"
								className={classes.headerActionBtn}
								startIcon={<GetAppIcon style={{ fontSize: 15 }} />}
								onClick={handleDownload}
							>
								Download PDF
							</Button>
						)}
						<Button
							variant="outlined"
							size="small"
							className={classes.headerActionBtn}
							startIcon={<ArrowBackIcon style={{ fontSize: 15 }} />}
							onClick={() => navigate("/docForms")}
						>
							Back to forms
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}

function ActivityTimeline() {
	const classes = useStyles();

	const events = useMemo(() => {
		const session = (() => {
			try { return JSON.parse(localStorage.getItem("esignature") || "{}"); } catch { return {}; }
		})();

		const items: { label: string; time?: string }[] = [];

		items.push({ label: "You submitted the form" });

		if (session.isMagicLink) {
			items.push({ label: "Opened via email link" });
		}

		items.push({ label: "Form was sent to you" });

		return items;
	}, []);

	return (
		<>
			{events.map((ev, i) => (
				<div key={i} className={classes.activityItem}>
					<span className={classes.activityDot} />
					<div>
						<span className={classes.activityText}>{ev.label}</span>
						{ev.time && <span className={classes.activityTime}>{ev.time}</span>}
					</div>
				</div>
			))}
		</>
	);
}

function DocFormFiller() {
	const { id, idRequest } = useParams();
	const navigate = useNavigate();
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [loadingMessage, setLoadingMessage] = useState("");
	const { docFormFiller, getDocFormFillerData, fields, urlPdf, values, setValue, flowId, isLoading: isLoadingForm, error: loadError } = useDocFormFiller();
	const df = docFormFiller as any;
	const templateName: string =
		df?.new?.form?.[0]?.templateName ||
		df?.templateName ||
		df?.doc?.name ||
		"";
	const [readOnly, setReadOnly] = useState(isMagicLinkFormCompleted());
	const [magicLinkChecked, setMagicLinkChecked] = useState(!isMagicLinkSession());

	const { isConnected, sessionId, error: wsError, sendFieldUpdate, sendCheckFlowStatus } = useFormCollaboration(
		readOnly ? null : idRequest,
		null,
		readOnly ? null : flowId
	);

	const { signatureImage, getSignatureList } = useSignatureForms();

	// State and ref for scroll synchronization
	const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
	const scrollToFieldRef = useRef<((fieldId: string) => void) | null>(null);

	const handleFieldFocus = useCallback((fieldId: string) => {
		setActiveFieldId(fieldId);
		if (scrollToFieldRef.current) {
			scrollToFieldRef.current(fieldId);
		}
	}, []);

	const handleFieldInView = useCallback((fieldId: string) => {
		setActiveFieldId(fieldId);
		if (scrollToFieldRef.current) {
			scrollToFieldRef.current(fieldId);
		}
	}, []);

	useEffect(() => {
		let cancelled = false;
		const init = async () => {
			if (isMagicLinkSession()) {
				const status = await syncMagicLinkSessionFromValidate();
				if (cancelled) return;
				if (status?.expired) {
					toast.error("Your secure link has expired.");
					navigate("/");
					return;
				}
				if (status?.completed) {
					setReadOnly(true);
				}
				setMagicLinkChecked(true);
			} else {
				setMagicLinkChecked(true);
			}
			if (id && idRequest) {
				await getDocFormFillerData(id, idRequest);
			}
		};
		init();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, idRequest]);

	useEffect(() => {
		if (loadError) {
			toast.error("Error loading form. Please try again.");
		}
	}, [loadError]);

	useEffect(() => {
		if (wsError) {
			console.error('Collaboration error:', wsError);
		}
	}, [wsError]);

	useEffect(() => {
		if (isConnected && sessionId) {
			console.log('Collaboration active - Session:', sessionId);
		}
	}, [isConnected, sessionId]);

	const handleReject = async () => {
		navigate("/docForms");
	};

	// Gate submit on the same rules as the sidebar progress bar (API often omits fieldRequired).
	const canSubmitForm = useMemo(() => {
		if (!values.length) return false;
		return values.every(isFieldCompleted);
	}, [values]);

	const validateRequiredFields = () => {
		const incomplete = values.filter((field) => !isFieldCompleted(field));
		if (incomplete.length > 0) {
			const fieldNames = incomplete.map((field: { label: string }) => field.label).join(", ");
			toast.error(`Please complete all fields: ${fieldNames}`);
			return false;
		}
		return true;
	};

	const handleComplete = async () => {
		if (readOnly) {
			toast.info("This form was already submitted.");
			navigate("/docForms");
			return;
		}
		// Validar campos requeridos antes de enviar
		if (!validateRequiredFields()) {
			return;
		}

		setLoading(true);
		setLoadingMessage("Filling form...");
		try {
			/*
			 values:[
				{
					fieldId: "1",
					fieldValue: "value1",
					fieldType: "text",
				}
			 ]
			 */
			const valuesToSend = values.map((value: {id: string, value: string, fieldType: string}) => ({
				fieldId: value.id,
				fieldValue:
					value.fieldType === "checkbox"
						? normalizeCheckboxValue(value.value)
						: value.value,
				fieldType: value.fieldType,
			}));
			await fillDocForm(idRequest, valuesToSend);
			markMagicLinkFormCompleted();
			// Notify WS only after fill succeeded so backend sees completed state
			if (sendCheckFlowStatus) {
				sendCheckFlowStatus();
			}
			setLoadingMessage("Form filled successfully");
			navigate("/docForms");
			toast.success("Form submitted successfully. You can return with your email link until it expires.");
		} catch (error) {
			console.error('Error filling doc form:', error);
			if (
				typeof error === "object" &&
				error !== null &&
				"response" in error &&
				typeof (error as any).response === "object" &&
				(error as any).response !== null &&
				"data" in (error as any).response &&
				typeof (error as any).response.data === "object" &&
				(error as any).response.data !== null &&
				"detail" in (error as any).response.data
			) {
				toast.warning((error as any).response.data.detail);
			} else {
				toast.warning("Error filling form");
			}
		} finally {
			setLoading(false);
		}
	};

	const handleFieldChange = (fieldId: string, value: string, fieldType?: string) => {
		setValue(fieldId, value);

		if (isConnected && sendFieldUpdate) {
			sendFieldUpdate(fieldId, value);
		}
	};

	const handleSaveSignature = async (signatureData: string) => {
		// Magic-link guests have no persistent profile. The drawn signature still
		// applies to the form field via handleCanvasSave; skip the profile API.
		if (isMagicLinkSession()) {
			return;
		}
		try {
			const response = await updateSignature(signatureData);
			if (response && response.success === true) {
				await getSignatureList();
				toast.success("Signature saved to profile successfully");
				return;
			}
			toast.error("Error saving signature to profile");
		} catch (error) {
			console.error('Error saving signature:', error);
			const apiError = error as { response?: { data?: { detail?: string; message?: string } } };
			toast.error(
				apiError?.response?.data?.detail ||
					apiError?.response?.data?.message ||
					"Error saving signature to profile"
			);
		}
	};

	return (
		<div className={classes.root}>
			<ToastContainer autoClose={3000} />
			<Loading
				show={!magicLinkChecked || isLoadingForm || loading}
				text={isLoadingForm ? "Loading form..." : loadingMessage}
			/>

			<Header readOnly={readOnly} templateName={templateName} urlPdf={urlPdf} />
			<div className={classes.container}>
				<div className={classes.pdfContainer}>
					<Paper
						elevation={0}
						className={classes.pdfPaper}
						style={readOnly ? { minHeight: 420 } : undefined}
					>
						{urlPdf && fields.length > 0 && (
							<DocumentEditor
								fileUrl={urlPdf}
								fields={fields}
								fieldValues={values}
								activeFieldId={readOnly ? null : activeFieldId}
								scrollToFieldRef={scrollToFieldRef}
								reviewMode={readOnly}
							/>
						)}
					</Paper>
				</div>
				<div className={classes.formContainer}>
					{readOnly ? (
						<>
							<div>
								<h2 className={classes.formTitle} style={{ fontSize: "0.95rem", color: "#374151" }}>
									Your answers
								</h2>
								<p className={classes.formSubtitle} style={{ marginBottom: 8 }}>
									Read only — submitted {new Date().toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
								</p>
							</div>
							<div className={classes.divider} />
							<div className={classes.formContent}>
								<SubmittedAnswersReview fields={fields} values={values} />
							</div>
							<div className={classes.activityTimeline}>
								<p className={classes.activityTitle}>Activity</p>
								<ActivityTimeline />
							</div>
						</>
					) : (
						<>
							<div>
								<h2 className={classes.formTitle}>Form</h2>
								<p className={classes.formSubtitle}>
									Complete the form below to complete the request
								</p>
							</div>
							<div className={classes.divider} />
							<div className={classes.formContent}>
								<DynamicForm
									values={values}
									onChange={handleFieldChange}
									signatureImage={signatureImage}
									onSaveSignature={handleSaveSignature}
									onFocus={handleFieldFocus}
									onFieldInView={handleFieldInView}
									activeFieldId={activeFieldId}
									signerDisplayName={getSignerDisplayNameFromStorage()}
								/>
							</div>
							<div className={classes.buttonContainer}>
								<Button
									variant="contained"
									className={classes.completeButton}
									onClick={handleComplete}
									size="large"
									color="primary"
									disabled={!canSubmitForm || isLoadingForm || loading}
									title={
										!canSubmitForm && values.length > 0
											? "Complete required fields to submit. Checkboxes are optional."
											: undefined
									}
								>
									Complete & Submit
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

export default DocFormFiller;
